import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";
import { MobileNav } from "@/components/mobile-nav";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { UserProfileHeader } from "@/components/user-profile-header";
import { CreditSummaryCard } from "@/components/credit-summary-card";
import { SavedAccountsGrid } from "@/components/saved-accounts-grid";
import { CreditStatsDashboard } from "@/components/credit-stats-dashboard";
import {
  MessageSquare,
  AlertCircle,
  RefreshCw,
  LogOut,
  BarChart3,
  Users,
  Settings,
  Home,
  CreditCard,
  ExternalLink,
} from "lucide-react";
import { Preloader } from "@/components/ui/preloader";
import { useAuth } from "@/router";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useCredits } from "@/hooks/use-credits";
import { AccountQuotaIndicator } from '@/components/account-quota-indicator'; // 추가된 부분

// Import settings tabs components
import { NotificationSettingsTab } from "@/components/settings-tabs/notification-settings-tab";
import { AppearanceSettingsTab } from "@/components/settings-tabs/appearance-settings-tab";

// Add TypeScript declaration for Twitter and Google conversion tracking
declare global {
  interface Window {
    twq: any;
    gtag?: any;
  }
}

// Define a type for saved accounts
interface SavedAccount {
  id: string;
  username: string;
  full_name?: string;
  profile_pic_url?: string;
  profile_data?: any; // 기존 인터페이스와 호환되도록 유지
  created_at?: string; // 기존 인터페이스와 호환되도록 유지
}

// Tab types
type TabInfo = {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
};

export function ProfilePage() {
  const { session } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savedAccounts, setSavedAccounts] = useState<SavedAccount[]>([]);
  const [userInfo, setUserInfo] = useState({
    email: "",
    createdAt: "",
    totalChats: 0,
  });
  const [isSigningOut, setIsSigningOut] = useState(false);

  const {
    availableCredits,
    totalCredits,
    refreshCredits,
    transactions,
    loading: creditsLoading,
  } = useCredits();

  // 함수 선언을 초기화하여 호이스팅 문제 해결
  const fetchUserData = useCallback(async () => {
    if (!session?.user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // 테이블 스키마에 맞게 쿼리 수정 (full_name, profile_pic_url 열이 없음)
      const { data: savedAccountsData, error: savedAccountsError } = await supabase
        .from("saved_instagram_accounts")
        .select("id, username, profile_data, created_at") // 실제 존재하는 열만 선택
        .eq("user_id", session.user.id)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (
        savedAccountsError &&
        savedAccountsError.code !== "PGRST116"
      ) {
        console.error("Error fetching accounts:", savedAccountsError);
        setLoadError(
          (prev) => prev || "저장된 계정 정보를 불러오는데 실패했습니다."
        );
      } else {
        setSavedAccounts(savedAccountsData || []);
      }

      // 사용자 기본 정보만 설정 (채팅 데이터 로드 제거)
      setUserInfo({
        email: session.user.email || "",
        createdAt: session.user.created_at || "",
        totalChats: 0,  // 채팅 데이터 로드 대신 기본값 사용
      });

      setIsLoading(false);
    } catch (error: unknown) {
      console.error("General error fetching user data:", error);
      setLoadError("데이터를 로드하는 중 오류가 발생했습니다.");
      toast({
        title: "데이터 로드 실패",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
      setIsLoading(false);
    }
  }, [session, toast]);

  const refreshData = useCallback(() => {
    console.log("Refreshing profile data");
    setIsLoading(true);
    setLoadError(null);
    fetchUserData();
    refreshCredits();
  }, [refreshCredits, fetchUserData]);

  useEffect(() => {
    const success = searchParams.get("success");
    const canceled = searchParams.get("canceled");

    if (success === "true") {
      // Google Ads 구독결제 전환 이벤트 추적
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-16672097897/3RTxCIrzpcEaEOmM8I0-',
          'value': 4900.0,
          'currency': 'KRW'
        });
        console.log('Google Ads 구독결제 전환 이벤트 전송됨');
      }

      // Twitter 전환 추적 이벤트 코드 실행
      if (typeof window !== 'undefined' && window.twq) {
        window.twq('event', 'tw-pqvmf-pqvmg', {
          value: 4900.0, // 결제 가격 (원화)
          conversion_id: Date.now().toString(), // 유니크 ID로 타임스탬프 사용
          email_address: session?.user?.email || null // 사용자 이메일
        });
        console.log('Twitter 결제완료 전환 이벤트 전송됨');
      }

      toast({
        title: "결제 성공",
        description:
          "결제가 성공적으로 처리되었습니다. 크레딧이 추가되었습니다.",
      });
      refreshData();
      setSearchParams({}, { replace: true });
    } else if (canceled === "true") {
      toast({
        title: "결제 취소",
        description: "결제가 취소되었습니다.",
        variant: "destructive",
      });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, toast, refreshData, setSearchParams, session?.user?.email]);

  // Define tabs with icons and colors
  const tabs: TabInfo[] = [
    {
      id: "summary",
      label: "요약",
      icon: <Home className="h-4 w-4" />,
      color: "from-blue-500 to-indigo-500",
    },
    {
      id: "credits",
      label: "크레딧",
      icon: <BarChart3 className="h-4 w-4" />,
      color: "from-green-500 to-emerald-500",
    },
    {
      id: "accounts",
      label: "계정",
      icon: <Users className="h-4 w-4" />,
      color: "from-orange-500 to-amber-500",
    },
    {
      id: "settings",
      label: "설정",
      icon: <Settings className="h-4 w-4" />,
      color: "from-violet-500 to-purple-500",
    },
  ];

  // Get tab from URL or use default
  const tabFromUrl = searchParams.get("tab");
  const validTabs = tabs.map((tab) => tab.id);
  const defaultTab =
    tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : "summary";

  // Active tab state
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Handle tab change
  const handleTabChange = useCallback(
    (tabId: string) => {
      console.log("Tab changed to:", tabId);
      setActiveTab(tabId);
      setSearchParams({ tab: tabId }, { replace: true });
    },
    [setSearchParams]
  );

  // Update active tab when URL changes
  useEffect(() => {
    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      console.log("Updating active tab from URL:", tabFromUrl);
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, validTabs]);

  {/*const handleManageSubscription = () => {
    window.location.href = 'https://billing.stripe.com/p/login/eVa2cb4f0gUoaqY7ss';
  };*/}

  // Fetch initial data on component mount
  useEffect(() => {
    console.log("Initial fetch triggered");
    fetchUserData();
  }, [fetchUserData]);

  const handleSignOut = useCallback(async () => {
    if (isSigningOut) return;

    setIsSigningOut(true);

    try {
      console.log('로그아웃 시작');

      // 세션 확인 후 로그아웃 시도
      if (session) {
        await supabase.auth.signOut();
        console.log("Supabase 로그아웃 성공");
      } else {
        console.log("세션이 없는 상태: 로컬 정리만 진행");
      }

      // 세션 상태와 관계없이 로컬 스토리지 정리
      try {
        localStorage.removeItem("supabase.auth.token");
        localStorage.removeItem("supabase.auth.expires_at");
        localStorage.removeItem("supabase.auth.refresh_token");
        // 더 확실한 정리를 위해 추가 항목도 제거
        localStorage.removeItem("sb-serorkllzefbtfdkatei-auth-token");
        localStorage.removeItem("supabase.auth.event");

        console.log("로컬 스토리지 정리 완료");
      } catch (e) {
        console.error("로컬 스토리지 정리 실패:", e);
      }

      // 토스트 메시지
      toast({
        title: "로그아웃 성공",
        description: "안전하게 로그아웃되었습니다."
      });

      // 페이지 이동 - 두 가지 방식으로 시도
      setTimeout(() => {
        try {
          navigate("/auth", { replace: true });

          // 직접 이동도 백업으로 시도
          setTimeout(() => {
            window.location.href = "/auth";
          }, 100);
        } catch (e) {
          console.error("라우터 이동 실패, 직접 이동 시도:", e);
          window.location.href = "/auth";
        }
      }, 300);
    } catch (error: any) {
      console.error("로그아웃 오류:", error);

      // 오류가 발생해도 로컬 스토리지는 정리하고 강제 이동
      try {
        localStorage.removeItem("supabase.auth.token");
        localStorage.removeItem("supabase.auth.expires_at");
        localStorage.removeItem("supabase.auth.refresh_token");
        localStorage.removeItem("sb-serorkllzefbtfdkatei-auth-token");
      } catch (e) {
        console.error("오류 후 로컬 스토리지 정리 실패:", e);
      }

      toast({
        title: "로그아웃 중 문제 발생",
        description: "강제 로그아웃을 시도합니다.",
        variant: "destructive",
      });

      // 오류 발생해도 강제 이동
      setTimeout(() => {
        window.location.href = "/auth";
      }, 500);
    }
  }, [isSigningOut, toast, navigate, session]);

  // 로그아웃 버튼 직접 DOM 참조
  const logoutButtonRef = useRef<HTMLButtonElement>(null);

  // 로그아웃 버튼 직접 이벤트 설정
  useEffect(() => {
    const button = logoutButtonRef.current;

    if (button) {
      // 추가 이벤트 리스너 (최후의 수단)
      const clickHandler = () => {
        console.log("로그아웃 버튼 네이티브 클릭 감지");
        handleSignOut();
      };

      button.addEventListener("click", clickHandler);

      return () => {
        button.removeEventListener("click", clickHandler);
      };
    }
  }, [handleSignOut]);

  if (!session) {
    return (
      <div className="flex min-h-screen flex-col">
        <header className="container border-b py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold">프로필</h1>
            <ThemeToggle />
          </div>
        </header>
        <main className="container flex-1 flex items-center justify-center">
          <Card className="w-full max-w-sm">
            <CardHeader className="text-center">
              <CardTitle>로그인 필요</CardTitle>
              <CardDescription>
                프로필을 보려면 로그인이 필요합니다.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button
                onClick={() => navigate("/auth")}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
              >
                로그인 페이지로 이동
              </Button>
            </CardFooter>
          </Card>
        </main>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="container border-b py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">프로필</h1>
          <ThemeToggle />
        </div>
      </header>

      <main className="container flex-1 space-y-6 py-8 pb-24 md:pb-24">
        {isLoading ? (
          <Preloader size="medium" />
        ) : loadError ? (
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>데이터 로드 오류</AlertTitle>
              <AlertDescription className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span>{loadError}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refreshData}
                  className="mt-2 shrink-0 sm:mt-0 sm:ml-2"
                >
                  <RefreshCw className="mr-2 h-3 w-3" />
                  다시 시도
                </Button>
              </AlertDescription>
            </Alert>
            <Card>
              <CardHeader>
                <CardTitle>제한된 프로필 정보</CardTitle>
                <CardDescription>
                  일부 데이터를 불러오지 못했습니다.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium">이메일</p>
                  <p className="text-sm text-muted-foreground">
                    {session.user.email}
                  </p>
                </div>
                <div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate("/")}
                  >
                    홈으로 이동
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <UserProfileHeader
                email={userInfo.email}
                createdAt={userInfo.createdAt}
                totalChats={userInfo.totalChats}
                savedAccountsCount={savedAccounts.length}
                availableCredits={availableCredits}
                hasActiveSubscription={false}
                initialAvatarUrl={session?.user?.user_metadata?.avatar_url || null}
                initialPersona={session?.user?.user_metadata?.persona || null}
              />
            </div>

            <div className="mt-6">
              {/* Enhanced Tab Navigation */}
              <div
                className="mb-6 rounded-xl overflow-hidden border shadow-sm relative z-10"
                style={{ pointerEvents: 'auto' }}
              >
                <div className="flex overflow-x-auto scrollbar-hide">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`relative flex-1 flex flex-col items-center px-4 py-3 min-w-[100px] transition-all duration-200
                        ${activeTab === tab.id ? "bg-card text-foreground" : "bg-muted/50 text-muted-foreground hover:bg-muted"}
                      `}
                      style={{ pointerEvents: 'auto', position: 'relative', zIndex: 15 }}
                    >
                      <div
                        className={`flex items-center justify-center ${activeTab === tab.id ? "text-primary" : "text-muted-foreground"}`}
                      >
                        {tab.icon}
                      </div>
                      <span className="mt-1 text-sm">{tab.label}</span>

                      {/* Active indicator */}
                      {activeTab === tab.id && (
                        <motion.div
                          layoutId="activeTab"
                          className={`absolute bottom-0 h-0.5 w-full bg-gradient-to-r ${tab.color}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                          style={{ pointerEvents: 'none' }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content without Motion - 클릭 이벤트가 작동하도록 수정 */}
              <div className="relative" style={{ position: 'relative', zIndex: 5, pointerEvents: 'auto' }}>
                {activeTab === "summary" && (
                  <div className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      <CreditSummaryCard
                        availableCredits={availableCredits}
                        totalCredits={totalCredits}
                        isLoading={creditsLoading}
                        onRefresh={refreshCredits}
                      />

                      {/* 계정 연결 쿼터 카드 추가 */}
                      <AccountQuotaIndicator />
                    </div>

                    {availableCredits !== null && availableCredits < 10 && (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>크레딧이 부족합니다</AlertTitle>
                        <AlertDescription className="flex items-center justify-between">
                          <span>대화를 계속하려면 크레딧을 충전하세요.</span>
                          <Button
                            size="sm"
                            variant="link"
                            className="h-auto p-0 text-primary"
                            onClick={() => navigate("/pricing")}
                          >
                            충전하러 가기
                          </Button>
                        </AlertDescription>
                      </Alert>
                    )}

                    <Card className="overflow-hidden">
                      <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
                        <CardTitle>빠른 작업</CardTitle>
                      </CardHeader>
                      <CardContent className="grid gap-4 sm:grid-cols-2 pt-6">
                        <Button
                          onClick={() => navigate("/")}
                          variant="outline"
                          className="h-auto justify-start gap-3 p-4 text-left hover:shadow-md transition-all"
                        >
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <MessageSquare className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <span className="font-medium">
                              새 대화 시작하기
                            </span>
                            <span className="block text-xs text-muted-foreground mt-0.5">
                              인스타그램 계정 검색
                            </span>
                          </div>
                        </Button>

                        {/*
       <Button
                          onClick={handleManageSubscription}
                          variant="outline"
                          className="h-auto justify-start gap-3 p-4 text-left hover:shadow-md transition-all"
                        >
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <CreditCard className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <span className="font-medium">구독 관리</span>
                            <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                              결제 및 구독 설정
                              <ExternalLink className="h-3 w-3 ml-0.5" />
                            </span>
                          </div>
                        </Button>
*/}

                        <Button
                          onClick={() => window.location.href = "mailto:daniel1st@daniel1stai.com"}
                          variant="outline"
                          className="h-auto justify-start gap-3 p-4 text-left hover:shadow-md transition-all"
                        >
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <CreditCard className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <span className="font-medium">고객 센터</span>
                            <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                              고객 센터로 이동
                            </span>
                          </div>
                        </Button>

                        <Button
                          onClick={() => window.location.href = "https://buy.stripe.com/14A00ibFfgBrfdr4nTcAo1v"}
                          variant="outline"
                          className="h-auto justify-start gap-3 p-4 text-left hover:shadow-md transition-all"
                        >
                          <div className="h-8 w-8 rounded-full bg-destructive/10 flex items-center justify-center">
                            <LogOut className="h-4 w-4 text-destructive" />
                          </div>
                          <div>
                            <span className="font-medium">즉시 탈퇴 및 개인정보 보호 패스</span>
                            <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                              개인정보 보호 및 데이터 삭제
                              <ExternalLink className="h-3 w-3 ml-0.5" />
                            </span>
                          </div>
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {activeTab === "credits" && (
                  <CreditStatsDashboard
                    availableCredits={availableCredits}
                    totalCredits={totalCredits}
                    transactions={transactions}
                    isLoading={creditsLoading}
                  />
                )}

                {activeTab === "accounts" && (
                  <div className="space-y-4" style={{ position: 'relative', zIndex: 5, pointerEvents: 'auto' }}>
                    {/* 계정 쿼터 정보 표시 */}
                    <AccountQuotaIndicator />

                    <SavedAccountsGrid
                      accounts={savedAccounts}
                      onAccountsChanged={refreshData}
                    />
                  </div>
                )}

                {activeTab === "settings" && (
                  <div className="overflow-hidden border shadow-md rounded-lg" style={{ position: 'relative', zIndex: 5, pointerEvents: 'auto' }}>
                    <div className="bg-gradient-to-r from-violet-500/5 to-purple-500/5 p-6">
                      <h3 className="font-semibold leading-none tracking-tight">계정 설정</h3>
                      <p className="text-sm text-muted-foreground">
                        개인 계정 정보 및 앱 설정을 관리하세요
                      </p>
                    </div>

                    <div className="p-0">
                      <div className="border-b">
                        <div className="container py-6">
                          <h3 className="text-lg font-medium mb-4">
                            계정 정보
                          </h3>
                          <div className="space-y-4">
                            <div className="rounded-md bg-muted/50 p-4">
                              <p className="text-sm font-medium mb-1">이메일</p>
                              <p className="text-base break-all">
                                {session.user.email || "이메일 정보 없음"}
                              </p>
                            </div>
                            <div className="rounded-md bg-muted/50 p-4">
                              <p className="text-sm font-medium mb-1">가입 일자</p>
                              <p className="text-base">
                                {new Date(
                                  session.user.created_at || ""
                                ).toLocaleDateString("ko-KR", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border-b">
                        <div className="container py-6" style={{ pointerEvents: 'auto' }}>
                          <h3 className="text-lg font-medium mb-4">앱 설정</h3>
                          <div className="space-y-4" style={{ pointerEvents: 'auto' }}>
                            <div style={{ pointerEvents: 'auto' }}>
                              <AppearanceSettingsTab />
                            </div>
                            <div style={{ pointerEvents: 'auto' }}>
                              <NotificationSettingsTab />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-center border-t pt-6 pb-6 p-6">
                      {/* 로그아웃 버튼 (안정적인 구현) */}
                      <div className="w-full max-w-xs">
                        <button
                          ref={logoutButtonRef}
                          type="button"
                          onClick={handleSignOut}
                          disabled={isSigningOut}
                          aria-label="로그아웃"
                          className="w-full text-destructive border border-destructive/30 bg-background hover:bg-destructive/10 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring h-9 px-4 py-2"
                        >
                          {isSigningOut ? (
                            <>
                              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                              로그아웃 중...
                            </>
                          ) : (
                            <>
                              <LogOut className="mr-2 h-4 w-4" />
                              로그아웃
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <MobileNav />
    </div>
  );
}